import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import UserAvatar from '../ui/UserAvatar';
import { useChatSearchStore } from '../../store/chatSearchStore';
import ChatSearchResultBody from './ChatSearchResultBody';

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString([], { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/**
 * One search result. Click (or Enter/Space) jumps to the message in the
 * conversation; while that jump is paging history the card shows a spinner,
 * and when it ran out of its page budget, "Keep loading" continues it.
 */
export default function ChatSearchResultCard({ message, channelId, terms }) {
  const { t } = useTranslation();
  const m = message;
  const seq = m.server_seq;
  const status = useChatSearchStore((s) => (s.jumpSeq === seq ? s.jumpStatus : 'idle'));
  const jump = () => {
    if (seq != null) useChatSearchStore.getState().requestJump(channelId, seq);
  };
  const name = m.sender?.name || 'Unknown';

  // The card is ONE interactive element (role=button); the jump status and
  // its "Keep loading" button sit BELOW it, never nested inside (review R7).
  return (
    <div className="chat-search-card-wrap">
      <div
        role="button"
        tabIndex={0}
        className="chat-search-card"
        data-result-seq={seq ?? undefined}
        onClick={jump}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            jump();
          }
        }}
      >
        <UserAvatar className="chat-search-card-avatar" userId={m.sender_id} name={name} size={24} />
        <div className="chat-search-card-main">
          <div className="chat-search-card-head">
            <span className="chat-search-card-name">{name}</span>
            <span className="chat-search-card-time">{formatWhen(m.inserted_at)}</span>
            <span className="chat-search-card-jump">{t('chat.search.jump')}</span>
          </div>
          <ChatSearchResultBody message={m} terms={terms} channelId={channelId} />
        </div>
      </div>
      {status !== 'idle' && (
        <div className="chat-search-card-status" role="status">
          {status === 'loading' && (
            <>
              <Loader2 size={12} className="chat-search-spin" /> {t('chat.search.jumpLoading')}
            </>
          )}
          {status === 'far' && (
            <>
              {t('chat.search.jumpFar')}{' '}
              <button type="button" className="chat-search-link-btn" onClick={jump}>
                {t('chat.search.keepLoading')}
              </button>
            </>
          )}
          {status === 'missing' && t('chat.search.jumpMissing')}
        </div>
      )}
    </div>
  );
}
